import { useCallback, useEffect, useRef, useState } from 'react';
import { goi, LoiApi, tien } from '../chung/api';
import { useHoiLai } from '../chung/hoi-lai';
import { useKetNoi } from '../chung/KetNoi';
import type { Don, NhomSanPham, SanPham, TrangSanPham } from '../chung/kieu';
import { AnhSanPham, MaDoiChieu } from '../chung/AnhSanPham';
import { HopGhiChu } from '../chung/HopGhiChu';

/**
 * Màn hình nhân viên tư vấn — làm cho ĐIỆN THOẠI, cầm một tay, đứng cạnh khách.
 *
 * Ba trạng thái, không có trạng thái thứ tư:
 *
 *   1. Chưa giữ giỏ nào  → bàn phím số, gõ mã khách
 *   2. Đang giữ một giỏ  → thêm hàng · sửa số lượng · gửi thu ngân
 *   3. Vừa gửi xong      → màn xác nhận, rồi về (1)
 *
 * 🔴 TIẾP QUẢN là thứ quan trọng nhất ở màn này.
 *
 *    Hai nhân viên gõ cùng một mã thì người gõ sau vào giỏ, người trước bị đẩy
 *    ra — khách đã đi từ quầy A sang quầy B. Giao diện PHẢI tự phát hiện và
 *    thoát ngay, không đợi người dùng bấm rồi mới báo lỗi.
 *
 *    Bỏ bước này thì A vẫn bấm thêm hàng vào một giỏ không còn của mình, server
 *    chặn bằng 409 — nhưng lúc đó A đã nói với khách là "đã thêm rồi".
 */
export function ManTuVan() {
  const [gioId, datGioId] = useState<number | null>(null);
  const [ketThuc, datKetThuc] = useState<{ kieu: 'xong' | 'da-huy'; ma: string; tong: number } | null>(null);

  /*
    🔴 KHÔNG còn `/api/gio/dang-giu` (bỏ 04/09).

       Không ai "đang giữ" giỏ nữa, nên không có gì để hỏi. Đổi lại, tải lại
       trang là về bàn phím số — và điều đó CHẤP NHẬN ĐƯỢC vì gõ lại đúng mã 4 số
       là vào lại đúng giỏ cũ, cùng hàng, cùng tiền. Trước đây phải khôi phục cho
       bằng được vì giỏ gắn chặt với một người; giờ thì không.
  */

  const roiGio = useCallback((k: NonNullable<typeof ketThuc>) => {
    datGioId(null);
    datKetThuc(k);
  }, []);

  return (
    /*
     * 🔴 Chan be ngang lai, khong de man nay keo dan het man hinh.
     *
     *    Ba ten mien deu tro vao cung mot ban dung, nen `seller.` mo tren may
     *    ban la chuyen se xay ra. Da nhin thay that: ban phim so keo rong 2400px,
     *    moi phim to bang ban tay. Cot hep can giua vua dung tren dien thoai vua
     *    doc duoc tren may ban.
     */
    <div
      style={{
        flex: 1,
        minHeight: 0,
        width: '100%',
        maxWidth: 560,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {ketThuc ? (
        <ManKetThuc k={ketThuc} tiepTuc={() => datKetThuc(null)} />
      ) : gioId === null ? (
        <ManNhapMa moXong={(g) => datGioId(g.id)} dangTai={false} />
      ) : (
        <ManGio gioId={gioId} roiGio={roiGio} />
      )}
    </div>
  );
}

// ───────────────────────── 1. gõ mã khách ─────────────────────────

const PHIM = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'xoa', '0', 'go'];

function ManNhapMa({ moXong, dangTai }: { moXong: (g: Don) => void; dangTai: boolean }) {
  const [ma, datMa] = useState('');
  const [loi, datLoi] = useState<string | null>(null);
  const [dangMo, datDangMo] = useState(false);
  const { song } = useKetNoi();

  const mo = async () => {
    if (!ma || dangMo || !song) return;
    datDangMo(true);
    datLoi(null);
    try {
      const g = await goi<Don>('/api/gio/mo', { method: 'POST', than: { ma } });
      moXong(g);
    } catch (e) {
      datLoi(e instanceof LoiApi ? e.message : 'Không gọi được máy chủ.');
    } finally {
      datDangMo(false);
    }
  };

  const bam = (p: string) => {
    datLoi(null);
    if (p === 'xoa') datMa((c) => c.slice(0, -1));
    else if (p === 'go') void mo();
    /*
     * 🔴 ĐÚNG 4 chữ số — đây là luật của server, không phải con số tôi chọn.
     *
     *    `POST /api/gio/mo` trả 400 *"Mã ... không hợp lệ — phải là 1 đến 4 chữ
     *    số"*. Bản đầu tôi chặn ở 6 vì đoán; nhân viên gõ 5 số, bấm MỞ GIỎ, rồi
     *    mới nhận lỗi — đo tại chỗ mới thấy. Chặn ngay ở phím thì không gõ thừa
     *    được, và ô hiện sẵn bốn gạch nên mắt biết trước cần mấy số.
     *
     *    Server tự đệm số 0 ở đầu: gõ `42` thành `0042`.
     */
    else datMa((c) => (c.length >= 4 ? c : c + p));
  };

  // Bàn phím thật vẫn phải chạy — có người dùng máy tính bảng có bàn phím rời.
  useEffect(() => {
    const f = (e: KeyboardEvent) => {
      if (e.key >= '0' && e.key <= '9') bam(e.key);
      else if (e.key === 'Backspace') bam('xoa');
      else if (e.key === 'Enter') bam('go');
    };
    window.addEventListener('keydown', f);
    return () => window.removeEventListener('keydown', f);
  });

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 14, minHeight: 0 }}>
      <div className="noi troi-len" style={{ padding: '22px 20px', textAlign: 'center', flexShrink: 0 }}>
        <div className="nhan-chu">Mã khách</div>
        <div
          className="lom"
          style={{
            marginTop: 12,
            padding: '18px 20px',
            minHeight: 76,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <span
            className="so"
            style={{
              fontSize: 42,
              fontWeight: 800,
              letterSpacing: '0.14em',
              color: ma ? 'var(--tien)' : 'var(--chu-3)',
              lineHeight: 1,
            }}
          >
            {ma || '– – – –'}
          </span>
        </div>
        {loi && (
          <div
            role="alert"
            className="lom-nho hien-len"
            style={{
              marginTop: 14,
              padding: '12px 14px',
              color: 'var(--nhan-dam)',
              fontSize: 'var(--chu-nho)',
              fontWeight: 600,
            }}
          >
            {loi}
          </div>
        )}
      </div>

      <div
        className="noi troi-len"
        style={{
          flex: 1,
          minHeight: 0,
          padding: 18,
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 14,
          alignContent: 'center',
          animationDelay: '60ms',
        }}
      >
        {PHIM.map((p) => (
          <button
            key={p}
            onClick={() => bam(p)}
            disabled={dangMo || dangTai || (p === 'go' && (!ma || !song)) || (p === 'xoa' && !ma)}
            className={p === 'go' ? 'nut-chinh' : undefined}
            aria-label={p === 'xoa' ? 'Xoá một chữ số' : p === 'go' ? 'Xác nhận' : p}
            style={{
              // Phím vuông, to — ngón cái bấm khi đang đứng, không nhìn kỹ.
              aspectRatio: '1.35',
              fontSize: p === 'go' ? 15 : 26,
              fontWeight: 700,
              borderRadius: 'var(--bo-goc)',
              padding: 0,
            }}
          >
            {p === 'xoa' ? '⌫' : p === 'go' ? (dangMo ? '…' : 'OK') : p}
          </button>
        ))}
      </div>

      
    </div>
  );
}

// ───────────────────────── 2. giỏ đang giữ ─────────────────────────

function ManGio({
  gioId,
  roiGio,
}: {
  gioId: number;
  roiGio: (k: { kieu: 'xong' | 'da-huy'; ma: string; tong: number }) => void;
}) {
  const [the, datThe] = useState<'gio' | 'tim'>('gio');
  const [loi, datLoi] = useState<string | null>(null);
  const [hoiXong, datHoiXong] = useState(false);
  const [moGhiChu, datMoGhiChu] = useState(false);
  const { song } = useKetNoi();

  const { duLieu: gio, taiLai } = useHoiLai<Don>(`/api/gio/${gioId}`);
  // "Món" là SỐ LƯỢNG (2 + 1 = 3), không phải số dòng (2). Chủ dự án chốt 14/09:
  // khách mua 2 thỏi cùng màu là 2 món. `so_mon` ở thành tích cũng đếm theo số lượng.
  const soMon = gio?.dong.reduce((t, d) => t + d.so_luong, 0) ?? 0;

  /*
    Giỏ chỉ còn MỘT lý do bị đá ra: thu ngân huỷ nó.

    🔴 Không còn phát hiện "bị tiếp quản" — từ 04/09 nhiều người cùng làm chung
       một giỏ, nên thấy người khác thêm hàng vào là chuyện BÌNH THƯỜNG, không
       phải dấu hiệu mình bị đẩy ra. Cả đường phụ dựa vào `/api/gio/dang-giu`
       cũng bỏ theo.

    `daRoi` chặn gọi `roiGio` hai lần: một nhịp hỏi có thể về sau khi đã thoát,
    và khi đó màn kết thúc bị dựng lại đè lên chính nó.
  */
  const daRoi = useRef(false);
  useEffect(() => {
    if (!gio || daRoi.current) return;
    if (gio.trang_thai === 'DA_HUY') {
      daRoi.current = true;
      roiGio({ kieu: 'da-huy', ma: gio.ma, tong: gio.tien.tong });
    }
  }, [gio, roiGio]);

  /*
    🔴 "Khách hàng tiếp theo" KHÔNG gọi máy chủ.

       Không còn bước gửi: giỏ vẫn mở nguyên đó cho tới khi thu ngân xác nhận
       đơn hàng. Nút này chỉ đưa nhân viên về bàn phím số để phục vụ khách sau —
       nên nó không hỏng được, và cũng không cần khoá khi mất mạng.
  */
  const khachTiepTheo = () => {
    if (!gio) return;
    daRoi.current = true;
    roiGio({ kieu: 'xong', ma: gio.ma, tong: gio.tien.tong });
  };

  if (!gio) {
    return (
      <div className="noi" style={{ flex: 1, display: 'grid', placeItems: 'center', color: 'var(--chu-3)' }}>
        Đang mở giỏ…
      </div>
    );
  }

  // Giỏ đã gửi cho thu ngân: xem được, không sửa được. Đây là đường thứ ba trong
  // hợp đồng — nhân viên gõ lại mã của một giỏ đã gửi thì rơi vào đây.
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 14, minHeight: 0 }}>
      <div
        className="noi troi-len"
        style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}
      >
        <div>
          <div className="nhan-chu">Mã khách</div>
          <div className="so" style={{ fontSize: 28, fontWeight: 800, letterSpacing: '0.06em', lineHeight: 1.2 }}>
            {gio.ma}
          </div>
        </div>
        <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
          <div className="nhan-chu">{soMon} món</div>
          <div className="tien" style={{ fontSize: 22, lineHeight: 1.2 }}>
            {tien(gio.tien.tong)}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, flexShrink: 0 }}>
        <button
          onClick={() => datThe('gio')}
          className={the === 'gio' ? 'lom-nho' : undefined}
          style={{ flex: 1, fontWeight: 700, color: the === 'gio' ? 'var(--nhan)' : undefined }}
        >
          Giỏ hàng {soMon > 0 && `(${soMon})`}
        </button>
        <button
          onClick={() => datThe('tim')}
          className={the === 'tim' ? 'lom-nho' : undefined}
          style={{ flex: 1, fontWeight: 700, color: the === 'tim' ? 'var(--nhan)' : undefined }}
        >
          Tìm hàng
        </button>
      </div>

      {loi && (
        <div
          role="alert"
          className="lom-nho hien-len"
          style={{
            padding: '12px 16px',
            color: 'var(--nhan-dam)',
            fontSize: 'var(--chu-nho)',
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {loi}
        </div>
      )}

      {the === 'gio' ? (
        <ThePhieu gio={gio} song={song} taiLai={taiLai} datLoi={datLoi} />
      ) : (
        <TheTimHang gioId={gio.id} song={song} taiLai={taiLai} datLoi={datLoi} />
      )}

      {the === 'gio' && (
        /*
          Hai nút ngang (14/09): TIẾP THEO bên trái, GHI CHÚ bên phải.

          TIẾP THEO không khoá theo `song`: nó chỉ đổi màn hình trong máy, không
          gọi máy chủ. Mất mạng vẫn phải chuyển sang khách sau được.

          🔴 Và KHÔNG khoá khi giỏ rỗng nữa — đổi 14/09 theo yêu cầu chủ dự án.
             Trước đó khoá với lý do "giỏ trống treo trong hàng đợi thu ngân".
             Nhưng khoá thì nhân viên mở nhầm mã, hoặc khách đổi ý không mua gì,
             là KHÔNG CÓ ĐƯỜNG RA khỏi giỏ — phải thêm một món rồi bớt đi. Đó là
             bẫy nặng hơn cái giỏ trống. Giỏ rỗng thì đi thẳng, không hỏi lại:
             không có gì để xác nhận. Cùng mã mở lại sẽ nhận đúng giỏ trống đó
             (bất biến 4.6), nên cũng không sinh rác.

          GHI CHÚ ghi vào `Order.note` qua PATCH /api/gio/:id/ghi-chu — thu ngân
          đã hiện sẵn ô "Ghi chú" trên phiếu. Cần mạng, nên khoá theo `song`.
        */
        <div style={{ display: 'flex', gap: 12, flexShrink: 0 }}>
          {/* Cùng cỡ với hai thẻ "Giỏ hàng / Tìm hàng" phía trên (14/09) — trước đó
              nút to gấp rưỡi, chiếm chỗ của dòng hàng cuối trên điện thoại. */}
          <button
            className="nut-chinh"
            style={{ flex: 1, fontWeight: 700 }}
            onClick={() => (gio.dong.length === 0 ? khachTiepTheo() : datHoiXong(true))}
          >
            Tiếp theo
          </button>
          <button
            style={{ flex: 1, fontWeight: 700, color: gio.ghi_chu ? 'var(--nhan)' : undefined }}
            disabled={!song}
            onClick={() => datMoGhiChu(true)}
            aria-label={gio.ghi_chu ? 'Sửa ghi chú' : 'Thêm ghi chú'}
          >
            {gio.ghi_chu ? 'Ghi chú ●' : 'Ghi chú'}
          </button>
        </div>
      )}

      {hoiXong && (
        <HopHoiXong
          gio={gio}
          dong={() => datHoiXong(false)}
          dongY={khachTiepTheo}
        />
      )}
      {moGhiChu && (
        <HopGhiChu
          gio={gio}
          dong={() => datMoGhiChu(false)}
          xong={() => {
            datMoGhiChu(false);
            taiLai();
          }}
        />
      )}
    </div>
  );
}

// ───────────────────────── thẻ: giỏ hàng ─────────────────────────

function ThePhieu({
  gio,
  song,
  taiLai,
  datLoi,
}: {
  gio: Don;
  song: boolean;
  taiLai: () => void;
  datLoi: (s: string | null) => void;
}) {
  // Ghi chú của khách hiện ngay trên thẻ giỏ, cùng dạng với phiếu thu ngân — nhân
  // viên phải thấy mình vừa ghi gì, và người thứ hai cùng làm giỏ cũng phải thấy.
  const khungGhiChu = gio.ghi_chu && (
    <div className="lom-nho hien-len" style={{ padding: '12px 16px', fontSize: 'var(--chu-nho)', textAlign: 'left', color: 'var(--chu)' }}>
      <div className="nhan-chu" style={{ marginBottom: 4 }}>
        Ghi chú
      </div>
      <div style={{ whiteSpace: 'pre-wrap' }}>{gio.ghi_chu}</div>
    </div>
  );

  if (gio.dong.length === 0) {
    return (
      <div
        className="noi hien-mo"
        style={{ flex: 1, display: 'grid', placeItems: 'center', padding: 30, textAlign: 'center', color: 'var(--chu-3)' }}
      >
        <div style={{ width: '100%' }}>
          <div className="gieng" style={{ width: 84, height: 84, fontSize: 34, margin: '0 auto' }}>
            ₫
          </div>
          <div style={{ marginTop: 18 }}>Giỏ chưa có món nào.
            <br />
            Sang thẻ <b>Tìm hàng</b> để thêm.
          </div>
          {khungGhiChu && <div style={{ marginTop: 22 }}>{khungGhiChu}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="noi" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 16 }}>
      {khungGhiChu && <div style={{ marginBottom: 14 }}>{khungGhiChu}</div>}
      {gio.dong.map((d, i) => (
        <DongPhieu
          key={d.id}
          gioId={gio.id}
          d={d}
          thuTu={i}
          song={song}
          taiLai={taiLai}
          datLoi={datLoi}
        />
      ))}

      {gio.tien.giam_tung_dong + gio.tien.giam_ca_don > 0 && (
        <div
          className="lom-nho"
          style={{ marginTop: 6, padding: '13px 16px', display: 'flex', justifyContent: 'space-between' }}
        >
          <span style={{ fontSize: 'var(--chu-nho)', color: 'var(--chu-2)' }}>
            Tạm tính {tien(gio.tien.truoc_giam)} · đã giảm
          </span>
          <span className="so" style={{ color: 'var(--nhan)', fontWeight: 700 }}>
            − {tien(gio.tien.giam_tung_dong + gio.tien.giam_ca_don)}
          </span>
        </div>
      )}
    </div>
  );
}

function DongPhieu({
  gioId,
  d,
  thuTu,
  song,
  taiLai,
  datLoi,
}: {
  gioId: number;
  d: Don['dong'][number];
  thuTu: number;
  song: boolean;
  taiLai: () => void;
  datLoi: (s: string | null) => void;
}) {
  const [dangSua, datDangSua] = useState(false);

  const dat = async (soLuong: number) => {
    if (dangSua || !song) return;
    datDangSua(true);
    datLoi(null);
    try {
      await goi(`/api/gio/${gioId}/dong/${d.id}`, { method: 'PATCH', than: { so_luong: soLuong } });
      taiLai();
    } catch (e) {
      datLoi(e instanceof LoiApi ? e.message : 'Không gọi được máy chủ.');
    } finally {
      datDangSua(false);
    }
  };

  return (
    <div
      className="noi-nho truot-phai"
      style={{ animationDelay: `${Math.min(thuTu, 8) * 40}ms`, padding: '14px 16px', marginBottom: 12 }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, lineHeight: 1.4 }}>{d.ten}</div>
          <div style={{ fontSize: 12, color: 'var(--chu-3)', marginTop: 3 }}>
            {d.sku} · {tien(d.don_gia)}/{d.don_vi}
            {d.giam > 0 && (
              <span style={{ color: 'var(--nhan)', fontWeight: 600 }}> · giảm {tien(d.giam)}</span>
            )}
          </div>
        </div>
        <span className="tien" style={{ fontSize: 'var(--chu-to)', flexShrink: 0 }}>
          {tien(d.thanh_tien)}
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
        <button
          onClick={() => void dat(d.so_luong - 1)}
          disabled={dangSua || !song}
          aria-label={d.so_luong === 1 ? 'Bỏ món này' : 'Bớt một'}
          style={{ width: 46, height: 46, padding: 0, fontSize: 20, fontWeight: 700, borderRadius: 'var(--tron)' }}
        >
          {/* Xuống 0 là BỎ DÒNG và nhả hàng về kho ngay — nên ở mức 1 thì đổi
            hẳn ký hiệu, đừng để nhân viên bấm trừ mà không ngờ món biến mất. */}
          {d.so_luong === 1 ? '🗑' : '−'}
        </button>
        <span className="so" style={{ minWidth: 40, textAlign: 'center', fontSize: 22, fontWeight: 800 }}>
          {d.so_luong}
        </span>
        <button
          onClick={() => void dat(d.so_luong + 1)}
          disabled={dangSua || !song}
          aria-label="Thêm một"
          style={{ width: 46, height: 46, padding: 0, fontSize: 20, fontWeight: 700, borderRadius: 'var(--tron)' }}
        >
          +
        </button>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--chu-3)' }}>{d.don_vi}</span>
      </div>
    </div>
  );
}

// ───────────────────────── thẻ: tìm hàng ─────────────────────────

function TheTimHang({
  gioId,
  song,
  taiLai,
  datLoi,
}: {
  gioId: number;
  song: boolean;
  taiLai: () => void;
  datLoi: (s: string | null) => void;
}) {
  const [q, datQ] = useState('');
  const [nhomId, datNhomId] = useState<number | null>(null);
  const [kq, datKq] = useState<SanPham[] | null>(null);
  const [dangTim, datDangTim] = useState(false);
  const [vuaThem, datVuaThem] = useState<number | null>(null);

  const nhom = useHoiLai<NhomSanPham[]>('/api/nhom', 60_000);

  // Gõ tới đâu tìm tới đó, nhưng chờ 250ms — không thì mỗi chữ cái là một
  // request, và mạng cửa hàng thì kết quả về không đúng thứ tự.
  useEffect(() => {
    if (!q.trim() && nhomId === null) {
      datKq(null);
      return;
    }
    let con = true;
    datDangTim(true);
    const h = setTimeout(async () => {
      try {
        const ts = new URLSearchParams();
        if (q.trim()) ts.set('q', q.trim());
        if (nhomId !== null) ts.set('nhom_id', String(nhomId));
        const d = await goi<TrangSanPham>(`/api/san-pham?${ts}`);
        if (con) datKq(d.danh_sach);
      } catch (e) {
        if (con) datLoi(e instanceof LoiApi ? e.message : 'Không tìm được.');
      } finally {
        if (con) datDangTim(false);
      }
    }, 250);
    return () => {
      con = false;
      clearTimeout(h);
    };
  }, [q, nhomId, datLoi]);

  const them = async (sp: SanPham) => {
    if (!song) return;
    datLoi(null);
    try {
      await goi(`/api/gio/${gioId}/them`, {
        method: 'POST',
        than: { san_pham_id: sp.id, so_luong: 1 },
      });
      taiLai();
      // Nhấp một cái ở đúng dòng vừa bấm: nhân viên thêm liên tiếp mấy món,
      // cần biết cái vừa rồi đã vào chứ không phải mình bấm hụt.
      datVuaThem(sp.id);
      // 1,9 s chứ không phải 0,9 s (14/09): trên điện thoại ở quầy, 0,9 s là chưa kịp
      // liếc xuống đã hết, nhân viên bấm thêm lần nữa vì tưởng chưa vào.
      setTimeout(() => datVuaThem((c) => (c === sp.id ? null : c)), 1900);
    } catch (e) {
      // Hết hàng trả 409 kèm số còn lại — hiện nguyên câu của server, đừng viết
      // lại, vì câu đó có con số nhân viên cần nói với khách.
      datLoi(e instanceof LoiApi ? e.message : 'Không thêm được.');
    }
  };

  return (
    <div className="noi" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ padding: '16px 16px 10px', flexShrink: 0 }}>
        <input
          value={q}
          onChange={(e) => datQ(e.target.value)}
          placeholder="Tên hàng hoặc mã — gõ không dấu cũng ra"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          aria-label="Tìm hàng"
        />
        <div
          style={{
            display: 'flex',
            gap: 9,
            marginTop: 12,
            overflowX: 'auto',
            paddingBottom: 4,
          }}
        >
          <button
            onClick={() => datNhomId(null)}
            className={nhomId === null ? 'lom-nho' : undefined}
            style={{ ...chipStyle, color: nhomId === null ? 'var(--nhan)' : undefined }}
          >
            Tất cả
          </button>
          {(nhom.duLieu ?? [])
            .filter((n) => n.active)
            .map((n) => (
              <button
                key={n.id}
                onClick={() => datNhomId((c) => (c === n.id ? null : n.id))}
                className={nhomId === n.id ? 'lom-nho' : undefined}
                style={{ ...chipStyle, color: nhomId === n.id ? 'var(--nhan)' : undefined }}
              >
                {n.name}
              </button>
            ))}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '4px 16px 16px' }}>
        {kq === null && (
          <div style={{ padding: '40px 10px', textAlign: 'center', color: 'var(--chu-3)', lineHeight: 1.7 }}>
            Gõ tên hàng, hoặc chọn một nhóm ở trên.
          </div>
        )}
        {kq !== null && kq.length === 0 && !dangTim && (
          <div style={{ padding: '40px 10px', textAlign: 'center', color: 'var(--chu-3)' }}>
            Không có mặt hàng nào khớp.
          </div>
        )}
        {(kq ?? []).map((sp, i) => (
          <button
            key={sp.id}
            onClick={() => void them(sp)}
            disabled={sp.het_hang || !song}
            className={`truot-phai ${vuaThem === sp.id ? 'lom-nho' : 'noi-nho'}`}
            style={{
              animationDelay: `${Math.min(i, 8) * 35}ms`,
              width: '100%',
              display: 'block',
              textAlign: 'left',
              padding: '14px 16px',
              marginBottom: 12,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <AnhSanPham anh={sp.anh} ten={sp.ten} co={56} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, lineHeight: 1.4 }}>{sp.ten}</div>
                <div style={{ fontSize: 12, color: 'var(--chu-3)', marginTop: 3 }}>
                  {sp.sku}
                  {sp.nhom && ` · ${sp.nhom}`}
                </div>
                <MaDoiChieu sapcode={sp.sapcode} barcode={sp.barcode} />
              </div>
              <span className="tien" style={{ flexShrink: 0 }}>
                {tien(sp.gia)}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
              {/*
                🔴 Hiện `con_ban`, KHÔNG phải số trên kệ.

                   `con_ban` đã trừ phần các giỏ khác đang giữ. Hiện số trên kệ
                   thì nhân viên hứa với khách một món đang nằm trong giỏ người
                   khác, và vỡ ở quầy thu ngân.
              */}
              {sp.het_hang ? (
                <span className="the the-nhan">Hết hàng</span>
              ) : (
                <span className="the the-mo">Còn {sp.con_ban} {sp.don_vi}</span>
              )}
              <span
                style={{
                  marginLeft: 'auto',
                  fontSize: 12,
                  fontWeight: 700,
                  color: vuaThem === sp.id ? 'var(--tot)' : sp.het_hang ? 'var(--chu-3)' : 'var(--nhan)',
                }}
              >
                {vuaThem === sp.id ? '✓ ĐÃ THÊM' : sp.het_hang ? '' : '+ THÊM'}
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

const chipStyle: React.CSSProperties = {
  padding: '8px 15px',
  fontSize: 13,
  fontWeight: 600,
  borderRadius: 'var(--tron)',
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

// ───────────────────────── hỏi lại trước khi gửi ─────────────────────────

/**
 * Gửi cho thu ngân là **một chiều** — gửi rồi nhân viên không lấy lại được, phải
 * nhờ thu ngân. Nên hỏi lại một câu, và nói rõ hậu quả chứ không hỏi "chắc chưa?".
 */
/**
 * Hỏi lại trước khi sang khách sau.
 *
 * 🔴 Câu chữ NGẮN hẳn so với bản cũ (đổi 04/09). Bản cũ phải cảnh báo "gửi rồi
 *    không sửa lại được" vì gửi là mất quyền thật. Giờ không mất gì cả: giỏ vẫn
 *    mở, quay lại gõ đúng mã là vào lại được. Giữ nguyên câu doạ cũ thì nhân
 *    viên ngần ngại trước một nút không có hậu quả gì — và cảnh báo nào cũng đọc
 *    thành tiếng ồn thì đến lúc có cảnh báo thật cũng không ai đọc.
 */
function HopHoiXong({
  gio,
  dong,
  dongY,
}: {
  gio: Don;
  dong: () => void;
  dongY: () => void;
}) {
  useEffect(() => {
    const f = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dong();
    };
    window.addEventListener('keydown', f);
    return () => window.removeEventListener('keydown', f);
  }, [dong]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={dong}
      className="hien-mo"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(126, 120, 128, 0.46)',
        backdropFilter: 'blur(4px)',
        display: 'grid',
        placeItems: 'center',
        padding: 20,
        zIndex: 50,
      }}
    >
      <div
        className="noi phong-len"
        onClick={(e) => e.stopPropagation()}
        style={{ width: '100%', maxWidth: 420, padding: 30, boxShadow: 'var(--noi-to)' }}
      >
        <div style={{ fontSize: 21, fontWeight: 700, marginBottom: 12 }}>Sang khách tiếp theo?</div>
        <div style={{ color: 'var(--chu-2)', marginBottom: 24, lineHeight: 1.65 }}>
          Giỏ <b className="so">{gio.ma}</b> — {gio.dong.reduce((t, d) => t + d.so_luong, 0)} món,{' '}
          <b className="tien">{tien(gio.tien.tong)}</b>.
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button style={{ flex: 1, padding: 15 }} onClick={dong}>
            Quay lại
          </button>
          <button className="nut-chinh" style={{ flex: 1, padding: 15 }} onClick={dongY}>
            SANG KHÁCH SAU
          </button>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────── 3. rời khỏi giỏ ─────────────────────────

function ManKetThuc({
  k,
  tiepTuc,
}: {
  k: { kieu: 'xong' | 'da-huy'; ma: string; tong: number };
  tiepTuc: () => void;
}) {
  const daGui = k.kieu === 'xong';
  return (
    <div
      className="noi troi-len"
      style={{ flex: 1, display: 'grid', placeItems: 'center', padding: 30, textAlign: 'center' }}
    >
      <div>
        <div
          className="gieng"
          style={{
            width: 96,
            height: 96,
            fontSize: 40,
            margin: '0 auto',
            color: daGui ? 'var(--tot)' : 'var(--nhan)',
          }}
        >
          {daGui ? '✓' : '!'}
        </div>

        <div style={{ fontSize: 22, fontWeight: 700, marginTop: 22 }}>
          {daGui ? 'Xác nhận' : 'Giỏ đã bị huỷ'}
        </div>

        <div style={{ color: 'var(--chu-2)', marginTop: 12, lineHeight: 1.7, maxWidth: 340 }}>
          {daGui ? (
            <>
              Giỏ <b className="so">{k.ma}</b> — <b className="tien">{tien(k.tong)}</b>. Mời khách tiếp tục trải nghiệm. Giỏ vẫn mở: gõ lại mã <b className="so">{k.ma}</b> là vào được.
            </>
          ) : (
            <>
              Giỏ <b className="so">{k.ma}</b> đã bị thu ngân huỷ, toàn bộ hàng đã nhả về kho.
            </>
          )}
        </div>

        <button
          className="nut-chinh"
          onClick={tiepTuc}
          style={{ marginTop: 30, padding: '16px 34px', fontSize: 'var(--chu-vua)' }}
        >
          KHÁCH TIẾP THEO
        </button>
      </div>
    </div>
  );
}

// ───────────────────────── giỏ đã gửi: CHỈ XEM ─────────────────────────

